import React, { useState, useEffect } from 'react'
import {
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Slider
} from '@mui/material'
import { useUpdateHumidorMutation } from '../../store/api/humidorApi'
import { useAppSelector } from '../../store/hooks'
import { Humidor } from '../../store/api/humidorApi'

interface HumidorEditFormProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  humidor: Humidor
}

interface FormData {
  name: string
  capacity: number
  location: string
  temperatureTarget: number | null
  humidityTarget: number | null
  notes: string
}

interface FormErrors {
  name?: string
  capacity?: string
  location?: string
  temperatureTarget?: string
  humidityTarget?: string
  notes?: string
}

const HumidorEditForm: React.FC<HumidorEditFormProps> = ({ open, onClose, onSuccess, humidor }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [updateHumidor, { isLoading, error }] = useUpdateHumidorMutation()

  const [formData, setFormData] = useState<FormData>({
    name: '',
    capacity: 50,
    location: '',
    temperatureTarget: null,
    humidityTarget: null,
    notes: ''
  })

  const [formErrors, setFormErrors] = useState<FormErrors>({})

  // Initialize form data when humidor changes
  useEffect(() => {
    if (humidor) {
      setFormData({
        name: humidor.name || '',
        capacity: humidor.capacity || 50,
        location: humidor.location || '',
        temperatureTarget: humidor.temperatureTarget || null,
        humidityTarget: humidor.humidityTarget || null,
        notes: humidor.notes || ''
      })
    }
  }, [humidor])

  const validateForm = (): boolean => {
    const errors: FormErrors = {}
    
    // Name validation - check if empty after trimming
    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Humidor name is required'
    } else if (formData.name.trim().length > 100) {
      errors.name = 'Name must be less than 100 characters'
    }

    // Capacity validation
    if (!formData.capacity || formData.capacity < 1) {
      errors.capacity = 'Capacity must be at least 1'
    } else if (formData.capacity > 10000) {
      errors.capacity = 'Capacity must be less than 10000'
    }

    // Temperature validation (if provided)
    if (formData.temperatureTarget !== null) {
      if (formData.temperatureTarget < -50 || formData.temperatureTarget > 100) {
        errors.temperatureTarget = 'Temperature must be between -50°F and 100°F'
      }
    }

    // Humidity validation (if provided)
    if (formData.humidityTarget !== null) {
      if (formData.humidityTarget < 0 || formData.humidityTarget > 100) {
        errors.humidityTarget = 'Humidity must be between 0% and 100%'
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value
    const parsedValue = field === 'capacity' ? (parseInt(value) || 0) : value
    
    setFormData(prev => ({
      ...prev,
      [field]: parsedValue
    }))

    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleSliderChange = (field: 'temperatureTarget' | 'humidityTarget') => (
    _event: Event,
    newValue: number | number[]
  ) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear field error when user adjusts slider
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const humidorData = {
        name: formData.name.trim(),
        capacity: formData.capacity,
        location: formData.location.trim() || undefined,
        temperatureTarget: formData.temperatureTarget || undefined,
        humidityTarget: formData.humidityTarget || undefined,
        notes: formData.notes.trim() || undefined
      }

      await updateHumidor({ 
        id: humidor.id, 
        updates: humidorData 
      }).unwrap()
      
      onSuccess?.()
      onClose()
    } catch (error) {
      // Error is handled by the mutation hook
      console.error('Failed to update humidor:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setFormErrors({})
      onClose()
    }
  }

  if (!isAuthenticated) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error">
            Please log in to edit humidors.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Humidor</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Humidor Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!formErrors.name}
                helperText={formErrors.name || ' '}
                required
                disabled={isLoading}
              />
            </Grid>

            {/* Capacity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={formData.capacity}
                onChange={handleInputChange('capacity')}
                error={!!formErrors.capacity}
                helperText={formErrors.capacity || 'Number of cigars'}
                inputProps={{ min: 1, max: 10000 }}
                required
                disabled={isLoading}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={handleInputChange('location')}
                error={!!formErrors.location}
                helperText={formErrors.location || 'Physical location'}
                disabled={isLoading}
              />
            </Grid>

            {/* Temperature Target */}
            <Grid item xs={12}>
              <Typography gutterBottom>
                Target Temperature: {formData.temperatureTarget || 65}°F
              </Typography>
              <Slider
                value={formData.temperatureTarget || 65}
                onChange={handleSliderChange('temperatureTarget')}
                min={60}
                max={75}
                step={1}
                marks={[
                  { value: 60, label: '60°F' },
                  { value: 65, label: '65°F' },
                  { value: 68, label: '68°F' },
                  { value: 70, label: '70°F' },
                  { value: 75, label: '75°F' }
                ]}
                disabled={isLoading}
              />
              {formErrors.temperatureTarget && (
                <Typography color="error" variant="caption">
                  {formErrors.temperatureTarget}
                </Typography>
              )}
            </Grid>

            {/* Humidity Target */}
            <Grid item xs={12}>
              <Typography gutterBottom>
                Target Humidity: {formData.humidityTarget || 68}%
              </Typography>
              <Slider
                value={formData.humidityTarget || 68}
                onChange={handleSliderChange('humidityTarget')}
                min={60}
                max={75}
                step={1}
                marks={[
                  { value: 60, label: '60%' },
                  { value: 65, label: '65%' },
                  { value: 68, label: '68%' },
                  { value: 70, label: '70%' },
                  { value: 75, label: '75%' }
                ]}
                disabled={isLoading}
              />
              {formErrors.humidityTarget && (
                <Typography color="error" variant="caption">
                  {formErrors.humidityTarget}
                </Typography>
              )}
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                error={!!formErrors.notes}
                helperText={formErrors.notes || 'Optional notes about this humidor'}
                disabled={isLoading}
              />
            </Grid>

            {/* Error Display */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">
                  Failed to update humidor. Please try again.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Updating...' : 'Update Humidor'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default HumidorEditForm
